import { onDestroy, onMount } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { Scroll } from "@lib/scroll";
import { Resize } from "@lib/subs";
import { gsapEase } from "@utils/easings";
import { clamp, map } from "@utils/math";
import { handleEditor } from "@webflow/detect-editor";

// Two modes on one markup, swapped at STACK_QUERY.
//
//   Stack — cards go position:sticky with staggered tops, each shrinking (and
//   optionally shading) as the next slides over it. Scroll is the only input,
//   so the controls are hidden here.
//
//   Pager — swipe or tap through the cards. They sit absolutely on top of each
//   other and travel one container width horizontally, so the motion is the
//   same whether the card is 700px or 2500px tall.
//
// A sticky card pins its TOP edge: one taller than the viewport parks with its
// bottom off-screen and that content can't be reached. Keep cards shorter than
// the viewport wherever the stack runs.
//
//   [data-module="opr-slider"]
//     [opr-slider="contain"]           <- card wrapper (falls back to the root)
//       [opr-slider="item"]            <- card
//         [opr-slider="header"]        <- card title; labels its anchor
//         [opr-slider="inner"]         <- optional scaled visual (falls back to item)
//         [opr-slider="shade"]         <- optional darkening overlay
//         [opr-slider="list-item"]     <- staggered on card change (pager only)
//     [opr-slider="nav-visible"]       <- optional; hidden in the pager, and
//                                         when there's only one card
//       [opr-slider="nav"]             <- rail; height synced to a card
//         [opr-slider="nav-block"]     <- the pill; leaving it hides the tip
//           [opr-slider="anchor"]      <- dot template, cloned one per card;
//                                         click goes to that card
//           [opr-slider="anchor-tip"]  <- one tooltip, slides to the hovered dot
//             [opr-slider="anchor-text"]  <- label; takes the card's header
//     [opr-slider="controls"]          <- pager only
//       [opr-slider="controls-prev"]
//       [opr-slider="controls-next"]
//       [opr-slider="index"]           <- optional; 1-based position
//       [opr-slider="total"]           <- optional; card count
//
// Optional tuning on the root:
//   data-stack-scale="0.85"   <- end scale for the first card (default 0.88)
//   data-stack-origin="top"   <- transform-origin: top | center | bottom

const STACK_QUERY = "(min-width: 768px)";

/* Stack */

const DEFAULT_MIN_SCALE = 0.88;
// Each card's sticky offset grows by this, so pinned tops peek out beneath the
// newest card.
const STACK_OFFSET_REM = 1.25;
// That band of edges is viewport the active card can't use, and it grows with
// every card. Past this total the step tightens instead.
const STACK_BAND_MAX_REM = 10;
// Shade holds at transparent until this fraction of the travel, then fades in.
const SHADE_START = 0.5;
const QUICK_CONFIG = { duration: 0.2, ease: "power2.out" };

/* Pager */

const MIN_VH = 0.9;

// The outgoing card trails this fraction of a width behind while it fades, so
// the swap reads as layers rather than a cut. It fades to zero because at rest
// it sits directly behind the active card, whose rounded corners would show it.
const TRAIL_RATIO = -0.25;
const TRAIL_OPACITY = 0;

const TRAVEL_EASE = gsapEase("energy");
const TRAVEL_DURATION = 0.36;
// Snapping back from an uncommitted drag is the system responding, not the user
// deciding — quicker than a deliberate move.
const SETTLE_DURATION = 0.22;

const LIST_Y = 12;
const LIST_STAGGER = 0.04;
const LIST_DURATION = 0.4;
const LIST_EASE = "power2.out";
const LIST_DELAY = 0.06;

// Styling hook for the ends of the list; matches news-slider.
const DISABLED_CLASS = "is--disabled";

/* Anchor nav */

const ANCHOR_ACTIVE_CLASS = "cc-active";
// The active dot stretches into a bar; the others return to their authored size.
const ACTIVE_ANCHOR_HEIGHT = "2.5rem";
const ANCHOR_DURATION = 0.35;
// The rail has to paint over the cards, which take z-index by depth. It stays
// inside the container's own stacking context (see CONTAIN_Z), so it can't
// climb over the site header.
const CONTAIN_Z = 1;
// Scale the tip appears from / disappears to.
const TIP_HIDDEN_SCALE = 0.8;
const TIP_IN_DURATION = 0.25;
const TIP_SLIDE_DURATION = 0.35;
const TIP_OUT_DURATION = 0.15;

/* Drag */

const DRAG_AXIS_LOCK_PX = 8;
// Commit on distance OR flick speed (px/ms).
const DRAG_COMMIT_RATIO = 0.25;
const DRAG_COMMIT_VELOCITY = 0.5;
const DRAG_EDGE_FRICTION = 0.3;

type Pose = {
  x: number;
  opacity: number;
};

type StackCard = {
  el: HTMLElement;
  inner: HTMLElement;
  shade: HTMLElement | null;
  stickStart: number;
  travel: number;
  minScale: number;
  setScaleX: gsap.QuickToFunc;
  setScaleY: gsap.QuickToFunc;
  setShade: gsap.QuickToFunc | null;
};

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const contain =
    element.querySelector<HTMLElement>('[opr-slider="contain"]') ?? element;
  const cards = Array.from(
    contain.querySelectorAll<HTMLElement>('[opr-slider="item"]'),
  );
  if (cards.length === 0) return;

  const total = cards.length;
  const controls = element.querySelector<HTMLElement>(
    '[opr-slider="controls"]',
  );
  const prevBtn = element.querySelector<HTMLElement>(
    '[opr-slider="controls-prev"]',
  );
  const nextBtn = element.querySelector<HTMLElement>(
    '[opr-slider="controls-next"]',
  );
  const indexLabel = element.querySelector<HTMLElement>('[opr-slider="index"]');
  const totalLabel = element.querySelector<HTMLElement>('[opr-slider="total"]');

  // Breakpoint-gated wrapper around the rail; falls back to the rail itself so
  // the single-card hide still works without it.
  const nav = element.querySelector<HTMLElement>('[opr-slider="nav"]');
  const navVisible =
    element.querySelector<HTMLElement>('[opr-slider="nav-visible"]') ?? nav;
  // The pill the dots sit in. Leaving it — not an individual dot — hides the
  // tip, so the tip stays up while the pointer travels down the rail.
  const navBlock =
    element.querySelector<HTMLElement>('[opr-slider="nav-block"]') ?? nav;
  // The authored dot: it becomes card 1's, and clones follow it in card order.
  const anchorTemplate =
    (navBlock ?? element).querySelector<HTMLElement>('[opr-slider="anchor"]') ??
    null;
  const anchorTip = element.querySelector<HTMLElement>(
    '[opr-slider="anchor-tip"]',
  );
  const anchorText =
    anchorTip?.querySelector<HTMLElement>('[opr-slider="anchor-text"]') ?? null;
  const anchorTextAuthored = anchorText?.textContent ?? "";

  const anchorLabels = cards.map(
    (card, index) =>
      card
        .querySelector<HTMLElement>('[opr-slider="header"]')
        ?.textContent?.trim() || `Principle ${index + 1}`,
  );

  const listItems = new Map<HTMLElement, HTMLElement[]>(
    cards.map((card) => [
      card,
      Array.from(
        card.querySelectorAll<HTMLElement>('[opr-slider="list-item"]'),
      ),
    ]),
  );

  const allListItems = [...listItems.values()].flat();

  const baseMinScale = clamp(
    0,
    1,
    parseFloat(dataset.stackScale ?? "") || DEFAULT_MIN_SCALE,
  );
  const stackOrigin = dataset.stackOrigin ?? "top";

  const isEditor = handleEditor();
  const stackQuery = window.matchMedia(STACK_QUERY);

  let mode: "stack" | "slider" | null = null;
  let modeResizeSub: (() => void) | null = null;

  function rootFontSize() {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  }

  // Per-card sticky step, tightened so the band of peeking edges never exceeds
  // STACK_BAND_MAX_REM.
  function stackStep() {
    const fontSize = rootFontSize();
    const step = STACK_OFFSET_REM * fontSize;
    if (total < 2) return step;
    return Math.min(step, (STACK_BAND_MAX_REM * fontSize) / (total - 1));
  }

  /* ---------------------------------------------------------------- stack */

  // The last card is never covered, so it takes the layout staggers but no
  // scale/shade animation.
  let stackCards: StackCard[] = [];
  // Scroll position at which each card pins — one per card, so the anchor rail
  // can read the last card too.
  let stickPoints: number[] = [];
  const stackStyles = new Map<HTMLElement, Partial<CSSStyleDeclaration>>();
  let stackScrollSub: (() => void) | null = null;
  let stackResizeSub: (() => void) | null = null;
  let layoutObserver: ResizeObserver | null = null;

  // Document-space top via the offsetTop chain — immune to the scale transform,
  // unlike getBoundingClientRect.
  function docTop(el: HTMLElement) {
    let top = 0;
    let node: HTMLElement | null = el;
    while (node) {
      top += node.offsetTop;
      node = node.offsetParent as HTMLElement | null;
    }
    return top;
  }

  // A pinned card's measurements include its sticky displacement. `static`
  // ignores `top`, and the read + revert happen in the same frame.
  function naturalTop(el: HTMLElement) {
    const prevPosition = el.style.position;
    el.style.position = "static";
    const top = docTop(el);
    el.style.position = prevPosition;
    return top;
  }

  // Scroll position at which a card pins. Read `top` before neutralizing —
  // static computes it to auto.
  function stickPoint(el: HTMLElement) {
    const stickyOffset = parseFloat(getComputedStyle(el).top) || 0;
    return naturalTop(el) - stickyOffset;
  }

  // Staggers the sticky tops and mirrors margins so cards un-pin together: each
  // card's bottom margin is sized to give it the last card's sticky footprint
  // (top offset + height + bottom margin), and negative top margins cancel the
  // flow shift that adds. Inline overrides are lifted before reading so authored
  // values come back fresh; all reads precede all writes, so nothing paints
  // mid-pass. Idempotent — safe on every resize.
  function applyStackLayout() {
    const offsetPx = stackStep();

    const measured = cards.map((el) => {
      const authored = stackStyles.get(el);
      el.style.top = authored?.top ?? "";
      el.style.marginTop = authored?.marginTop ?? "";
      el.style.marginBottom = authored?.marginBottom ?? "";
      el.style.minHeight = authored?.minHeight ?? "";
      const style = getComputedStyle(el);
      return {
        el,
        top: parseFloat(style.top) || 0,
        marginTop: parseFloat(style.marginTop) || 0,
        marginBottom: parseFloat(style.marginBottom) || 0,
        // Layout height: immune to the inner scale transform.
        height: el.offsetHeight,
      };
    });

    // Every card takes the tallest card's height, or a short card would leave
    // the taller one beneath it peeking out along the bottom. min-height, so a
    // later reflow grows the card instead of clipping it.
    const cardHeight = measured.reduce((max, m) => Math.max(max, m.height), 0);

    const last = measured[total - 1];
    const targetFootprint =
      last.top + (total - 1) * offsetPx + cardHeight + last.marginBottom;

    let prevAdded = 0;
    measured.forEach((m, index) => {
      const stickyTop = m.top + index * offsetPx;

      // These cards have no authored sticky offset, so card 0 needs its `top`
      // written too or it never pins.
      m.el.style.top = `${stickyTop}px`;
      m.el.style.minHeight = `${cardHeight}px`;
      if (index > 0) m.el.style.marginTop = `${m.marginTop - prevAdded}px`;

      // Deeper cards sit lower, so they need less margin to reach the same
      // footprint — possibly negative, which sticky honors.
      const added =
        index < total - 1
          ? targetFootprint - stickyTop - cardHeight - m.marginBottom
          : 0;
      if (index < total - 1) {
        m.el.style.marginBottom = `${m.marginBottom + added}px`;
      }
      prevAdded = added;
    });
  }

  // Each card scales over the distance until the next one pins and covers it.
  function measureStack() {
    stickPoints = cards.map(stickPoint);
    stackCards.forEach((card, i) => {
      card.stickStart = stickPoints[i];
      card.travel = Math.max(0, stickPoints[i + 1] - card.stickStart);
    });
  }

  function updateStack() {
    for (const card of stackCards) {
      if (card.travel <= 0) {
        card.setScaleX(1);
        card.setScaleY(1);
        card.setShade?.(0);
        continue;
      }
      const progress = clamp(
        0,
        1,
        (Scroll.scroll - card.stickStart) / card.travel,
      );
      const scale = map(progress, 0, 1, 1, card.minScale);
      card.setScaleX(scale);
      card.setScaleY(scale);
      card.setShade?.(clamp(0, 1, map(progress, SHADE_START, 1, 0, 1)));
    }
    setActiveAnchor(stackActiveIndex());
  }

  function setupStack() {
    cards.forEach((el, index) => {
      stackStyles.set(el, {
        position: el.style.position,
        top: el.style.top,
        marginTop: el.style.marginTop,
        marginBottom: el.style.marginBottom,
        minHeight: el.style.minHeight,
        zIndex: el.style.zIndex,
      });
      el.style.position = "sticky";
      // Later cards paint over earlier ones as they slide up.
      el.style.zIndex = String(index + 1);
    });
    applyStackLayout();

    // Sticky needs the visible scroll box: no clipping, no forced height.
    stackStyles.set(contain, {
      position: contain.style.position,
      height: contain.style.height,
      overflow: contain.style.overflow,
      zIndex: contain.style.zIndex,
    });
    contain.style.position = "relative";
    contain.style.height = "auto";
    contain.style.overflow = "visible";
    // Own stacking context, so the per-card z-indexes below (and the anchor
    // rail above them) stay local and never paint over the site header.
    contain.style.zIndex = String(CONTAIN_Z);

    // Scroll drives the stack, so the controls have nothing to do here.
    // !important because a Webflow breakpoint rule may set display on them.
    if (controls) {
      stackStyles.set(controls, { display: controls.style.display });
      controls.style.setProperty("display", "none", "important");
    }

    // Pinning is scroll position, not motion, so it survives reduced motion —
    // only the scrub is dropped, leaving stackCards empty and the update loops
    // as no-ops.
    stackCards = reduced
      ? []
      : cards.slice(0, -1).map((el, index) => {
          // Scale the inner wrapper so the sticky box keeps its layout size.
          const inner =
            el.querySelector<HTMLElement>('[opr-slider="inner"]') ?? el;
          const shade = el.querySelector<HTMLElement>('[opr-slider="shade"]');

          stackStyles.set(inner, {
            ...stackStyles.get(inner),
            willChange: inner.style.willChange,
            transformOrigin: inner.style.transformOrigin,
          });
          inner.style.willChange = "transform";
          inner.style.transformOrigin = `center ${stackOrigin}`;
          gsap.set(inner, { scale: 1 });
          if (shade) gsap.set(shade, { opacity: 0 });

          // Deeper cards shrink less: step each end scale toward 1 by an equal
          // share of the base-to-1 range.
          const minScale =
            total > 1
              ? clamp(
                  0,
                  1,
                  baseMinScale + (index / (total - 1)) * (1 - baseMinScale),
                )
              : baseMinScale;

          return {
            el,
            inner,
            shade,
            stickStart: 0,
            travel: 0,
            minScale,
            // quickTo can't drive "scale" (it expands to scaleX/scaleY), so one
            // per axis.
            setScaleX: gsap.quickTo(inner, "scaleX", QUICK_CONFIG),
            setScaleY: gsap.quickTo(inner, "scaleY", QUICK_CONFIG),
            setShade: shade
              ? gsap.quickTo(shade, "opacity", QUICK_CONFIG)
              : null,
          };
        });

    measureStack();
    updateStack();
    refreshAnchorNav();

    // Subscribed even under reduced motion: updateStack is a no-op with no
    // stackCards, but the anchor rail still has to follow the scroll.
    stackScrollSub = Scroll.add(updateStack);

    // Stick points are document positions, so anything reflowing content above
    // the stack invalidates them — lazy images and webfonts, most visibly after
    // refreshing below the cards. The window-resize sub can't catch those; the
    // window never resized. Re-applying is idempotent, so this settles in a
    // pass rather than feeding itself.
    layoutObserver = new ResizeObserver(() => {
      if (mode !== "stack") return;
      applyStackLayout();
      measureStack();
      updateStack();
      refreshAnchorNav();
    });
    layoutObserver.observe(document.body);

    stackResizeSub = Resize.add(() => {
      // Heights and authored offsets shift with the viewport, so rebuild the
      // staggers before re-measuring stick points.
      applyStackLayout();
      measureStack();
      updateStack();
      refreshAnchorNav();
    });
  }

  function teardownStack() {
    stackScrollSub?.();
    stackResizeSub?.();
    stackScrollSub = null;
    stackResizeSub = null;
    layoutObserver?.disconnect();
    layoutObserver = null;

    for (const card of stackCards) {
      // Kill before clearing — a mid-flight tween would keep writing
      // scaleX/scaleY for frames afterwards and leave the card stuck scaled.
      gsap.killTweensOf(card.inner);
      // GSAP can't clearProps "scale" directly.
      gsap.set(card.inner, { clearProps: "transform" });
      if (card.shade) {
        gsap.killTweensOf(card.shade);
        gsap.set(card.shade, { clearProps: "opacity" });
      }
    }
    stackCards = [];
    stickPoints = [];

    stackStyles.forEach((styles, el) => {
      for (const [prop, value] of Object.entries(styles)) {
        (el.style as any)[prop] = value ?? "";
      }
    });
    stackStyles.clear();
  }

  /* ---------------------------------------------------------------- pager */

  let activeIndex = 0;
  let tallest = 0;
  let cardTop = 0;
  let travelX = 0;
  let lastMeasuredWidth = window.innerWidth;
  let controlsDisplay = "";
  let navPagerDisplay = "";
  let sliderResizeSub: (() => void) | null = null;
  const sliderCleanups: Array<() => void> = [];

  // null until the gesture passes DRAG_AXIS_LOCK_PX and commits to an axis.
  let dragId: number | null = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartTime = 0;
  let dragAxis: "x" | "y" | null = null;

  // Lifts the absolute positioning to read natural flow metrics, then restores
  // it in the same frame. `cardTop` matters most: the cards don't start at the
  // top of the container — the controls row owns that space, and parking cards
  // at top:0 slides them underneath it.
  function measureContent() {
    gsap.set(cards, { clearProps: "position,top" });
    // The cards are authored position:sticky, so clearing our absolute
    // positioning hands them back to it — and a card that is pinned at that
    // moment reports its displaced offsetTop, which would then be reserved as
    // blank space above every card. `static` ignores sticky, and the read plus
    // the revert below happen in the same frame, so nothing paints.
    gsap.set(cards, { height: "auto", position: "static" });

    tallest = 0;
    cards.forEach((card) => {
      if (card.offsetHeight > tallest) tallest = card.offsetHeight;
    });
    cardTop = cards[0].offsetTop;

    gsap.set(cards, { position: "absolute", top: cardTop });
  }

  // The container's border-box width, not the card's own: cards are width:100%
  // of the padding box, so moving one by its own width leaves it short by the
  // right padding and that gap shows the next card's edge.
  function measureTravel() {
    travelX = contain.offsetWidth;
  }

  function applyHeight() {
    const height = Math.max(tallest, window.innerHeight * MIN_VH);
    gsap.set(contain, { height: cardTop + height });
    gsap.set(cards, { height });
  }

  // Cards ahead wait off the right edge, the card behind trails left. Reduced
  // motion crossfades in place — position change is what causes trouble.
  function getPose(distance: number): Pose {
    if (reduced) return { x: 0, opacity: distance === 0 ? 1 : 0 };
    if (distance < 0) {
      return { x: travelX * TRAIL_RATIO, opacity: TRAIL_OPACITY };
    }
    if (distance === 0) return { x: 0, opacity: 1 };
    return { x: travelX, opacity: 1 };
  }

  function updateControls() {
    const atStart = activeIndex === 0;
    const atEnd = activeIndex === total - 1;
    if (prevBtn) {
      prevBtn.classList.toggle(DISABLED_CLASS, atStart);
      prevBtn.setAttribute("aria-disabled", String(atStart));
    }
    if (nextBtn) {
      nextBtn.classList.toggle(DISABLED_CLASS, atEnd);
      nextBtn.setAttribute("aria-disabled", String(atEnd));
    }
    if (indexLabel) indexLabel.textContent = String(activeIndex + 1);
  }

  function staggerList() {
    const active = cards[activeIndex];
    if (!active) return;
    const items = listItems.get(active) ?? [];
    if (items.length === 0) return;

    gsap.fromTo(
      items,
      { opacity: 0, y: LIST_Y },
      {
        opacity: 1,
        y: 0,
        duration: LIST_DURATION,
        ease: LIST_EASE,
        stagger: LIST_STAGGER,
        delay: LIST_DELAY,
        overwrite: "auto",
      },
    );
  }

  function render(animate: boolean, fromIndex: number, duration?: number) {
    cards.forEach((card, i) => {
      const distance = i - activeIndex;
      const pose = getPose(distance);
      const fromDistance = i - fromIndex;
      // Cards that were off-screen and stay off-screen have nothing to show.
      const offscreen =
        (distance > 0 && fromDistance > 0) || (distance < 0 && fromDistance < 0);

      if (!animate || offscreen) {
        gsap.set(card, pose);
        return;
      }

      gsap.to(card, {
        ...pose,
        duration: duration ?? TRAVEL_DURATION,
        ease: TRAVEL_EASE,
        overwrite: "auto",
      });
    });

    if (!animate || reduced) {
      gsap.set(allListItems, { opacity: 1, y: 0 });
    } else if (activeIndex !== fromIndex) {
      staggerList();
    }

    updateControls();
    setActiveAnchor(activeIndex);
  }

  // The card is taller than the screen, so the reader is usually scrolled into
  // the middle of it. Land them at the top of the new one, not mid-sentence.
  function scrollCardIntoView() {
    const top = contain.getBoundingClientRect().top;
    if (top >= 0) return;
    window.scrollTo({
      top: window.scrollY + top,
      behavior: reduced ? "auto" : "smooth",
    });
  }

  function go(delta: number) {
    const target = activeIndex + delta;
    if (target < 0 || target > total - 1) {
      // Nothing there — nudge and come back, so the press is answered.
      const card = cards[activeIndex];
      if (!card || reduced) return;
      gsap
        .timeline({ overwrite: "auto" })
        .to(card, {
          x: travelX * -0.06 * delta,
          duration: 0.14,
          ease: "power2.out",
        })
        .to(card, { x: 0, duration: 0.4, ease: TRAVEL_EASE });
      return;
    }
    const from = activeIndex;
    activeIndex = target;
    scrollCardIntoView();
    render(true, from);
  }

  /* Drag */

  function dragTargetIndex(dx: number) {
    return dx < 0 ? activeIndex + 1 : activeIndex - 1;
  }

  // Moves the active card with the finger and brings its neighbour along at the
  // same rate, so the drag previews what releasing will commit to.
  function dragTo(dx: number) {
    const width = travelX || 1;
    const card = cards[activeIndex];
    const incoming = cards[dragTargetIndex(dx)];

    // No card that way: resist rather than hit a wall.
    if (!incoming) {
      gsap.set(card, { x: dx * DRAG_EDGE_FRICTION });
      return;
    }

    const progress = clamp(-1, 1, dx / width);
    gsap.set(card, { x: progress * width });
    if (progress < 0) {
      gsap.set(incoming, { x: width * (1 + progress), opacity: 1 });
    } else {
      gsap.set(incoming, {
        x: width * TRAIL_RATIO * (1 - progress),
        opacity: map(progress, 0, 1, TRAIL_OPACITY, 1),
      });
    }
  }

  function dragRelease(dx: number, elapsed: number) {
    const width = travelX || 1;
    const velocity = Math.abs(dx) / Math.max(elapsed, 1);
    const target = dragTargetIndex(dx);
    const committed =
      cards[target] !== undefined &&
      (Math.abs(dx) > width * DRAG_COMMIT_RATIO ||
        velocity > DRAG_COMMIT_VELOCITY);

    if (committed) {
      const from = activeIndex;
      activeIndex = target;
      scrollCardIntoView();
      render(true, from);
      return;
    }
    render(true, activeIndex, SETTLE_DURATION);
  }

  function onPointerDown(event: PointerEvent) {
    // One finger owns the gesture; later touches would jump the card.
    if (dragId !== null || reduced) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    dragId = event.pointerId;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    dragStartTime = performance.now();
    dragAxis = null;
  }

  function onPointerMove(event: PointerEvent) {
    if (event.pointerId !== dragId) return;
    const dx = event.clientX - dragStartX;
    const dy = event.clientY - dragStartY;

    if (dragAxis === null) {
      if (
        Math.abs(dx) < DRAG_AXIS_LOCK_PX &&
        Math.abs(dy) < DRAG_AXIS_LOCK_PX
      ) {
        return;
      }
      // Vertical wins ties: the card is taller than the screen, so scrolling is
      // the more common intent and must never be stolen.
      dragAxis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (dragAxis !== "x") {
        dragId = null;
        return;
      }
      // Keep receiving moves if the finger leaves the card.
      contain.setPointerCapture(dragId);
    }

    dragTo(dx);
  }

  function onPointerUp(event: PointerEvent) {
    if (event.pointerId !== dragId) return;
    const dx = event.clientX - dragStartX;
    const axis = dragAxis;
    if (contain.hasPointerCapture(dragId)) {
      contain.releasePointerCapture(dragId);
    }
    dragId = null;
    dragAxis = null;
    if (axis !== "x") return;
    dragRelease(dx, performance.now() - dragStartTime);
  }

  function setupSlider() {
    activeIndex = 0;
    gsap.set(contain, {
      position: "relative",
      overflow: "clip",
      // Own stacking context: the cards and the anchor rail stack against each
      // other here, not against the site header.
      zIndex: CONTAIN_Z,
      // Vertical scrolling stays native; horizontal is ours.
      touchAction: "pan-y",
    });
    lastMeasuredWidth = window.innerWidth;

    cards.forEach((card, i) => {
      gsap.set(card, {
        // Ascending, so the incoming card covers the one it replaces going
        // forward, and the outgoing one uncovers the card behind going back.
        zIndex: i + 1,
        left: 0,
        width: "100%",
      });
    });

    // Measures in flow, then lifts the cards out of it — has to run before
    // anything depends on their position.
    measureContent();
    applyHeight();
    measureTravel();

    if (controls) {
      controlsDisplay = controls.style.display;
      // Only intervene if they'd be invisible — authored as display:none to
      // avoid a flash, or left hidden by the stack's breakpoint rule. Anything
      // already visible keeps whatever layout it was given.
      if (getComputedStyle(controls).display === "none") {
        controls.style.setProperty("display", "flex", "important");
      }
      gsap.set(controls, { zIndex: total + 1 });
    }
    if (totalLabel) totalLabel.textContent = String(total);

    // The pager has its own controls, so the rail goes away here. !important
    // because a Webflow breakpoint rule may set display on it. Skipped when
    // there's one card — the rail is already hidden, and taking a reading of
    // that would lose the authored value.
    if (navVisible && total > 1) {
      navPagerDisplay = navVisible.style.display;
      navVisible.style.setProperty("display", "none", "important");
      // A pointer sitting on the rail as it vanishes never fires mouseleave, so
      // the tip would still be up the next time the rail is shown.
      hideTip();
    }

    if (!reduced) {
      contain.addEventListener("pointerdown", onPointerDown);
      contain.addEventListener("pointermove", onPointerMove);
      contain.addEventListener("pointerup", onPointerUp);
      contain.addEventListener("pointercancel", onPointerUp);
      sliderCleanups.push(() => {
        contain.removeEventListener("pointerdown", onPointerDown);
        contain.removeEventListener("pointermove", onPointerMove);
        contain.removeEventListener("pointerup", onPointerUp);
        contain.removeEventListener("pointercancel", onPointerUp);
        dragId = null;
        dragAxis = null;
      });
    }

    const wire = (btn: HTMLElement | null, delta: number, label: string) => {
      if (!btn) return;
      btn.setAttribute("role", "button");
      btn.setAttribute("tabindex", "0");
      btn.setAttribute("aria-label", label);
      gsap.set(btn, { userSelect: "none", touchAction: "manipulation" });

      const onClick = () => go(delta);
      const onKey = (event: KeyboardEvent) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        go(delta);
      };
      const onMouseDown = (event: MouseEvent) => {
        if (event.detail > 1) event.preventDefault();
      };

      btn.addEventListener("click", onClick);
      btn.addEventListener("keydown", onKey);
      btn.addEventListener("mousedown", onMouseDown);
      sliderCleanups.push(() => {
        btn.removeEventListener("click", onClick);
        btn.removeEventListener("keydown", onKey);
        btn.removeEventListener("mousedown", onMouseDown);
        gsap.set(btn, { clearProps: "userSelect,touchAction" });
        btn.classList.remove(DISABLED_CLASS);
        ["role", "tabindex", "aria-label", "aria-disabled"].forEach((attr) =>
          btn.removeAttribute(attr),
        );
      });
    };

    wire(prevBtn, -1, "Previous principle");
    wire(nextBtn, 1, "Next principle");

    render(false, activeIndex);
    refreshAnchorNav();

    const remeasure = () => {
      if (mode !== "slider") return;
      measureContent();
      applyHeight();
      measureTravel();
      render(false, activeIndex);
      refreshAnchorNav();
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(remeasure);
    }

    sliderResizeSub = Resize.add((data: { width: number }) => {
      if (mode !== "slider") return;
      // Content only reflows on a width change; a height-only change (mobile
      // URL bar) just needs the container re-fitted.
      if (data.width !== lastMeasuredWidth) {
        lastMeasuredWidth = data.width;
        measureContent();
      }
      applyHeight();
      measureTravel();
      render(false, activeIndex);
      refreshAnchorNav();
    });
  }

  function teardownSlider() {
    sliderResizeSub?.();
    sliderResizeSub = null;
    sliderCleanups.forEach((fn) => fn());
    sliderCleanups.length = 0;
    gsap.killTweensOf(cards);
    gsap.killTweensOf(allListItems);
    gsap.set(allListItems, { clearProps: "all" });
    gsap.set(cards, { clearProps: "all" });
    gsap.set(contain, { clearProps: "all" });
    if (controls) {
      gsap.set(controls, { clearProps: "all" });
      controls.style.display = controlsDisplay;
    }
    if (navVisible && total > 1) navVisible.style.display = navPagerDisplay;
  }

  /* ----------------------------------------------------------- anchor nav */

  // Ported from the product page's card-stack rail: one dot per card inside a
  // single pill, and one tip that slides between them. It belongs to the stack —
  // the pager hides it and navigates from its own controls — but it's built once
  // and stays wired for both, so nothing breaks if CSS ever shows it in the
  // pager: a dot scrolls to its card in stack mode and swaps to it in pager
  // mode, and the active dot follows whichever mode is running.

  const anchorClones: HTMLElement[] = [];
  const anchorCleanups: Array<() => void> = [];
  let navAnchors: HTMLElement[] = [];
  // Authored (inactive) dot height. Reads 0 while a breakpoint hides the rail;
  // the resize subs recover it.
  let anchorBaseHeight = 0;
  let anchorActive = -1;
  // Dot the tip is parked on, so height changes can re-center it; -1 while
  // hidden.
  let tipAnchor = -1;
  let tipVisible = false;
  let navDisplay = "";
  let navZIndex = "";
  let navHeight = "";

  const anchorDuration = (seconds: number) => (reduced ? 0 : seconds);

  // The rail centers its pill against a card, so it has to span one.
  // offsetHeight is layout height — the stack's scale transform can't skew it.
  function syncNavHeight() {
    if (nav && navAnchors.length) {
      nav.style.height = `${cards[0].offsetHeight}px`;
    }
  }

  // The rail can sit in a container CSS hides below its own breakpoint, where
  // offsetHeight reads 0. Re-measured with the inline height (from the active
  // tween) lifted, read and revert in the same frame so nothing paints.
  function measureAnchorBaseHeight() {
    const anchor = navAnchors[0] ?? anchorTemplate;
    if (!anchor) return 0;
    const prevHeight = anchor.style.height;
    anchor.style.height = "";
    const height = anchor.offsetHeight;
    anchor.style.height = prevHeight;
    return height;
  }

  // Vertical center a dot will have once in-flight height tweens settle. A
  // dot's top depends on the heights above it, so rebuild it from the first
  // dot's top (which never moves) plus each settled height and the static gaps
  // between dots. With nothing tweening this is the dot's current center.
  function anchorFinalCenterY(index: number) {
    const activeHeight = parseFloat(ACTIVE_ANCHOR_HEIGHT) * rootFontSize();
    const finalHeight = (i: number) =>
      i === anchorActive ? activeHeight : anchorBaseHeight;
    let top = navAnchors[0].getBoundingClientRect().top;
    for (let i = 0; i < index; i++) {
      // The gap between neighbours is flow spacing, untouched by the height
      // tweens, so reading it mid-tween is still exact.
      const rect = navAnchors[i].getBoundingClientRect();
      const next = navAnchors[i + 1].getBoundingClientRect();
      top += finalHeight(i) + (next.top - rect.bottom);
    }
    return top + finalHeight(index) / 2;
  }

  // Parks the tip's right edge on the dot's left edge, vertically centered.
  // Targets come off the tip's natural (untransformed) rect with the current
  // x/y subtracted out, so re-targeting mid-slide stays accurate.
  function moveTipTo(index: number) {
    const anchor = navAnchors[index];
    if (!anchorTip || !anchor) return;
    tipAnchor = index;

    // Swap the label first, then measure the tip at the new text's natural
    // width (inline width lifted, read and revert in the same frame). The x
    // target is computed against that final size while the label's width tweens
    // from its old one, so the tip resizes instead of jumping on the swap.
    let textFrom = 0;
    let textTo = 0;
    let tipRect: DOMRect;
    if (anchorText) {
      textFrom = anchorText.offsetWidth;
      anchorText.textContent = anchorLabels[index];
      const inlineWidth = anchorText.style.width;
      anchorText.style.width = "auto";
      textTo = anchorText.offsetWidth;
      tipRect = anchorTip.getBoundingClientRect();
      anchorText.style.width = inlineWidth;
    } else {
      tipRect = anchorTip.getBoundingClientRect();
    }

    const anchorRect = anchor.getBoundingClientRect();
    const currentX = (gsap.getProperty(anchorTip, "x") as number) || 0;
    const currentY = (gsap.getProperty(anchorTip, "y") as number) || 0;
    const targetX = currentX + (anchorRect.left - tipRect.right);
    // Center on where the dot will settle, not where it is — the hovered dot
    // may be mid height tween on its way in or out of active.
    const targetY =
      currentY + anchorFinalCenterY(index) - (tipRect.top + tipRect.height / 2);

    if (!tipVisible) {
      // First hover: appear in place at the dot, no cross-rail slide, label at
      // its natural width.
      tipVisible = true;
      if (anchorText) gsap.set(anchorText, { width: textTo });
      gsap.set(anchorTip, { x: targetX, y: targetY, scale: TIP_HIDDEN_SCALE });
      gsap.to(anchorTip, {
        autoAlpha: 1,
        scale: 1,
        duration: anchorDuration(TIP_IN_DURATION),
        ease: "power2.out",
        overwrite: "auto",
      });
      return;
    }

    // Same duration and ease for the slide and the width, so the tip's right
    // edge tracks the dot cleanly while it resizes.
    if (anchorText) {
      gsap.fromTo(
        anchorText,
        { width: textFrom },
        {
          width: textTo,
          duration: anchorDuration(TIP_SLIDE_DURATION),
          ease: "power3.out",
          overwrite: "auto",
        },
      );
    }
    gsap.to(anchorTip, {
      x: targetX,
      y: targetY,
      duration: anchorDuration(TIP_SLIDE_DURATION),
      ease: "power3.out",
      overwrite: "auto",
    });
  }

  function hideTip() {
    if (!anchorTip || !tipVisible) return;
    tipVisible = false;
    tipAnchor = -1;
    gsap.to(anchorTip, {
      autoAlpha: 0,
      scale: TIP_HIDDEN_SCALE,
      duration: anchorDuration(TIP_OUT_DURATION),
      ease: "power2.out",
      overwrite: "auto",
    });
  }

  function setActiveAnchor(index: number) {
    if (index === anchorActive || navAnchors.length === 0) return;
    anchorActive = index;

    navAnchors.forEach((anchor, i) => {
      const on = i === index;
      anchor.classList.toggle(ANCHOR_ACTIVE_CLASS, on);
      if (on) anchor.setAttribute("aria-current", "true");
      else anchor.removeAttribute("aria-current");
      // A base height of 0 means the rail was hidden when measured; tweening to
      // it would collapse the inactive dots. Leave heights alone until a resize
      // re-measures and forces a re-apply.
      if (anchorBaseHeight > 0) {
        gsap.to(anchor, {
          height: on ? ACTIVE_ANCHOR_HEIGHT : anchorBaseHeight,
          duration: anchorDuration(ANCHOR_DURATION),
          ease: "power2.out",
          overwrite: true,
        });
      }
    });

    // Those height tweens move a parked tip's center — its own dot resizing, or
    // one above it. Glide the tip to the settled center on the same curve so
    // they land together.
    if (anchorTip && tipVisible && tipAnchor >= 0) {
      const tipRect = anchorTip.getBoundingClientRect();
      const currentY = (gsap.getProperty(anchorTip, "y") as number) || 0;
      gsap.to(anchorTip, {
        y:
          currentY +
          anchorFinalCenterY(tipAnchor) -
          (tipRect.top + tipRect.height / 2),
        duration: anchorDuration(ANCHOR_DURATION),
        ease: "power2.out",
        overwrite: "auto",
      });
    }
  }

  // The card on top is the deepest one that has pinned. The 1px slack keeps it
  // active when a smooth scroll lands a hair short of its stick point.
  function stackActiveIndex() {
    let index = 0;
    for (let i = 0; i < stickPoints.length; i++) {
      if (Scroll.scroll >= stickPoints[i] - 1) index = i;
    }
    return index;
  }

  function anchorGo(index: number) {
    if (mode === "stack") {
      // Measured at click time — layout above the stack can shift after setup.
      window.scrollTo({
        top: stickPoint(cards[index]),
        behavior: reduced ? "auto" : "smooth",
      });
      return;
    }
    if (index === activeIndex) return;
    const from = activeIndex;
    activeIndex = index;
    scrollCardIntoView();
    render(true, from);
  }

  // Heights and the authored dot size depend on the viewport, and the rail may
  // have been hidden (measuring 0) when it was built.
  function refreshAnchorNav() {
    if (navAnchors.length === 0) return;
    syncNavHeight();
    if (anchorBaseHeight > 0) return;
    anchorBaseHeight = measureAnchorBaseHeight();
    if (anchorBaseHeight === 0) return;
    // Re-apply the heights setActiveAnchor skipped while the rail was hidden.
    const current = Math.max(anchorActive, 0);
    anchorActive = -1;
    setActiveAnchor(current);
  }

  function buildAnchorNav() {
    if (!anchorTemplate) return;

    // One card is nothing to navigate between. !important because a Webflow
    // breakpoint rule may set display on the wrapper.
    if (total < 2) {
      if (navVisible) {
        navDisplay = navVisible.style.display;
        navVisible.style.setProperty("display", "none", "important");
      }
      return;
    }

    // Above the cards, which take z-index by depth — but inside the container's
    // stacking context, so the site header still wins.
    if (navVisible) {
      navZIndex = navVisible.style.zIndex;
      navVisible.style.zIndex = String(total + 2);
    }
    if (nav) navHeight = nav.style.height;

    // Authored height is the inactive size, so capture it before anything goes
    // active. Reads 0 if the rail mounts hidden — recovered on resize.
    anchorBaseHeight = measureAnchorBaseHeight();

    // The authored dot is card 1's; clones follow it in card order.
    let last = anchorTemplate;
    navAnchors = cards.map((_, index) => {
      let anchor = anchorTemplate as HTMLElement;
      if (index > 0) {
        anchor = anchorTemplate!.cloneNode(true) as HTMLElement;
        last.after(anchor);
        anchorClones.push(anchor);
      }
      last = anchor;

      anchor.setAttribute("aria-label", anchorLabels[index]);
      // The rail sits inside the pager's drag area; a native link drag would
      // leave a ghost image trailing the pointer.
      anchor.setAttribute("draggable", "false");

      const onEnter = () => moveTipTo(index);
      const onClick = (event: Event) => {
        event.preventDefault();
        anchorGo(index);
      };
      anchor.addEventListener("mouseenter", onEnter);
      anchor.addEventListener("focus", onEnter);
      anchor.addEventListener("click", onClick);
      anchorCleanups.push(() => {
        anchor.removeEventListener("mouseenter", onEnter);
        anchor.removeEventListener("focus", onEnter);
        anchor.removeEventListener("click", onClick);
      });

      return anchor;
    });

    // The tip starts hidden and follows whichever dot is hovered. It stays put
    // while the pointer roams the pill; leaving the pill hides it.
    if (anchorTip && navBlock) {
      // Authored display:none keeps the tip from flashing before JS runs, but
      // autoAlpha only drives opacity/visibility — flip display here, while
      // it's still invisible, or every measurement reads 0.
      if (getComputedStyle(anchorTip).display === "none") {
        gsap.set(anchorTip, { display: "flex" });
      }
      gsap.set(anchorTip, {
        autoAlpha: 0,
        scale: TIP_HIDDEN_SCALE,
        // Right center: the tip scales from the edge touching the dot, holding
        // the two points moveTipTo measures still while it's scaled.
        transformOrigin: "right center",
      });
      // Nowrap + clip so the label measures and animates as one line.
      if (anchorText) {
        gsap.set(anchorText, { whiteSpace: "nowrap", overflow: "hidden" });
      }

      const onBlockLeave = () => hideTip();
      // Tabbing between dots leaves one for another inside the pill — only a
      // focus that lands outside it counts as leaving.
      const onBlockFocusOut = (event: FocusEvent) => {
        if (navBlock.contains(event.relatedTarget as Node | null)) return;
        hideTip();
      };
      navBlock.addEventListener("mouseleave", onBlockLeave);
      navBlock.addEventListener("focusout", onBlockFocusOut);
      anchorCleanups.push(() => {
        navBlock.removeEventListener("mouseleave", onBlockLeave);
        navBlock.removeEventListener("focusout", onBlockFocusOut);
      });
    }

    syncNavHeight();
  }

  function destroyAnchorNav() {
    anchorCleanups.forEach((fn) => fn());
    anchorCleanups.length = 0;

    if (anchorTip) {
      gsap.killTweensOf(anchorTip);
      // Clearing display returns the tip to its authored display:none.
      gsap.set(anchorTip, {
        clearProps: "transform,opacity,visibility,display",
      });
    }
    if (anchorText) {
      gsap.killTweensOf(anchorText);
      gsap.set(anchorText, { clearProps: "width,whiteSpace,overflow" });
      anchorText.textContent = anchorTextAuthored;
    }
    tipVisible = false;
    tipAnchor = -1;

    navAnchors.forEach((anchor) => {
      gsap.killTweensOf(anchor);
      gsap.set(anchor, { clearProps: "height" });
      anchor.classList.remove(ANCHOR_ACTIVE_CLASS);
      ["aria-current", "aria-label", "draggable"].forEach((attr) =>
        anchor.removeAttribute(attr),
      );
    });
    navAnchors = [];
    anchorActive = -1;
    anchorBaseHeight = 0;
    anchorClones.forEach((clone) => clone.remove());
    anchorClones.length = 0;

    if (nav) nav.style.height = navHeight;
    if (navVisible) {
      navVisible.style.display = navDisplay;
      navVisible.style.zIndex = navZIndex;
    }
  }

  /* ----------------------------------------------------------------- swap */

  // Both setup/teardown pairs restore every style they wrote, so the module can
  // switch modes live as the viewport crosses the breakpoint.
  function applyMode(next: "stack" | "slider") {
    if (mode === next) return;
    if (mode === "stack") teardownStack();
    if (mode === "slider") teardownSlider();
    mode = next;
    if (next === "stack") setupStack();
    else setupSlider();
  }

  function chooseMode() {
    applyMode(stackQuery.matches ? "stack" : "slider");
  }

  onMount(() => {
    // Built before either mode: both setups render the active dot.
    buildAnchorNav();

    // The stack reads Scroll, which the editor tears down — mount the pager
    // there so the cards stay laid out and clickable in the Designer.
    if (isEditor) {
      applyMode("slider");
      return;
    }

    chooseMode();
    stackQuery.addEventListener("change", chooseMode);
    // applyMode is idempotent, so a resize that doesn't cross the breakpoint is
    // a no-op.
    modeResizeSub = Resize.add(chooseMode);
  });

  onDestroy(() => {
    stackQuery.removeEventListener("change", chooseMode);
    modeResizeSub?.();
    modeResizeSub = null;
    if (mode === "stack") teardownStack();
    if (mode === "slider") teardownSlider();
    mode = null;
    destroyAnchorNav();
  });
}
